import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { FAQS, FAQ_GROUPS, type Faq } from '../data/faqs';

// ---------------------------------------------------------------------------
// Accordion item — animated open/close with max-height transition.
// ---------------------------------------------------------------------------

interface AccordionItemProps {
  faq: Faq;
  isOpen: boolean;
  onToggle: () => void;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ faq, isOpen, onToggle }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxH, setMaxH] = useState<number>(0);

  // Measure the natural height whenever the item opens (or content changes).
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setMaxH(contentRef.current.scrollHeight);
    }
  }, [isOpen]);

  return (
    <div className="border-b border-gray-100 dark:border-gray-800 last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="faq-summary flex items-start justify-between gap-4 py-4 w-full text-start cursor-pointer group"
      >
        <span
          className={`text-sm font-semibold leading-snug transition-colors ${
            isOpen
              ? 'text-violet-600 dark:text-violet-400'
              : 'text-gray-800 dark:text-gray-100 group-hover:text-gray-900 dark:group-hover:text-white'
          }`}
        >
          {faq.q}
        </span>
        <ChevronDown
          size={18}
          aria-hidden="true"
          className={`flex-shrink-0 mt-0.5 transition-transform duration-200 ${
            isOpen
              ? 'rotate-180 text-violet-500'
              : 'text-gray-400'
          }`}
        />
      </button>
      <div
        ref={contentRef}
        style={{ maxHeight: isOpen ? `${maxH}px` : '0px' }}
        className="overflow-hidden transition-[max-height] duration-300 ease-in-out"
      >
        <div className="pb-4 pe-8">
          <p data-speakable="true" className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{faq.a}</p>{/* AEO-3 */}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// A single group — section heading + accordion with one-at-a-time behavior.
// ---------------------------------------------------------------------------

interface FaqGroupSectionProps {
  label: string;
  items: Faq[];
}

const FaqGroupSection: React.FC<FaqGroupSectionProps> = ({ label, items }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = useCallback((i: number) => {
    setOpenIndex(prev => (prev === i ? null : i));
  }, []);

  return (
    <section className="mb-8 last:mb-0">
      <h2 className="text-base font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
        <span className="inline-block w-1 h-5 rounded-full bg-violet-500" aria-hidden="true" />
        {label}
      </h2>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 px-6 py-1 mt-2">
        {items.map((faq, i) => (
          <AccordionItem
            key={i}
            faq={faq}
            isOpen={openIndex === i}
            onToggle={() => toggle(i)}
          />
        ))}
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// FaqSection — the public component.
//
// `grouped=true`  → renders FAQ_GROUPS with section headings and managed state.
// `grouped=false` → renders a flat slice (homepage teaser) with native
//                   <details>/<summary> for no-JS support, identical to the
//                   previous implementation.
// ---------------------------------------------------------------------------

interface FaqSectionProps {
  /**
   * Render only the first N questions of the full FAQ array. Ignored when
   * `items` is supplied.
   */
  limit?: number;
  /**
   * Render exactly these questions instead of a slice of the full array. The
   * homepage passes its own set so the accordion and its FAQPage JSON-LD read
   * from the same source.
   */
  items?: Faq[];
  /** Use the grouped layout with section headings and managed accordion state. */
  grouped?: boolean;
}

const FaqSection: React.FC<FaqSectionProps> = ({ limit, items: itemsProp, grouped = false }) => {
  // ── Grouped mode (FAQ page) ───────────────────────────────────────────
  if (grouped) {
    return (
      <div>
        {FAQ_GROUPS.map((group, gi) => (
          <FaqGroupSection key={gi} label={group.label} items={group.items} />
        ))}
      </div>
    );
  }

  // ── Flat mode (homepage teaser) ───────────────────────────────────────
  // Uses native <details>/<summary> so it works without JS and before hydration.
  const items = itemsProp ?? (limit ? FAQS.slice(0, limit) : FAQS);
  return (
    <div className="divide-y divide-gray-100 dark:divide-gray-800">
      {items.map((faq, i) => (
        <details key={i} open className="faq-item group">
          <summary className="faq-summary flex items-start justify-between gap-4 py-4 cursor-pointer list-none">
            <span className="text-sm font-semibold leading-snug text-gray-800 dark:text-gray-100 group-hover:text-gray-900 dark:group-hover:text-white group-open:text-violet-600 dark:group-open:text-violet-400 transition-colors">
              {faq.q}
            </span>
            <ChevronDown
              size={18}
              aria-hidden="true"
              className="flex-shrink-0 mt-0.5 text-gray-400 transition-transform duration-200 group-open:rotate-180 group-open:text-violet-500"
            />
          </summary>
          <div className="pb-4 pe-8">
            <p data-speakable="true" className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{faq.a}</p>{/* AEO-3 */}
          </div>
        </details>
      ))}
    </div>
  );
};

export default FaqSection;
